import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  PartyPopper,
  Medal,
  Loader2,
  AlertTriangle,
  Clock,
  CalendarX2,
  Receipt,
  Euro,
  Calendar,
  Store,
  Sparkles,
} from "lucide-react";
import { useTranslation, Trans } from "react-i18next";
import { API } from "@/lib/api";
import { playerErrorMessage } from "@/lib/playerErrors";

/**
 * ClaimPage — public landing for the email "Récupérer mon gain" button.
 *
 * Design: Holographic Noir (dark premium, glassmorphism, subtle aurora glow).
 *
 * Behaviour:
 *   • On mount, POST /public/claim/{token}/open → auto-redeems the session
 *     (first valid visit only). Backend returns `just_redeemed` / `already_redeemed`.
 *   • First visit → "Bravo, vous avez gagné" + prize + merchant + coupon code.
 *   • Subsequent visits → "Lot déjà récupéré".
 *   • Not yet usable / expired / invalid → dedicated info states.
 */
export default function ClaimPage() {
  const { token } = useParams();
  const { i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  // Texts follow the language of the game the prize comes from
  const lang = data?.language || (i18n.language?.startsWith("en") ? "en" : "fr");
  const t = i18n.getFixedT(lang);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.post(`${API}/public/claim/${token}/open`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (err) return <ClaimFrame t={t}><InvalidState message={err} t={t} /></ClaimFrame>;
  if (!data)
    return (
      <ClaimFrame t={t}>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-white/70" />
        </div>
      </ClaimFrame>
    );

  // State routing
  if (data.already_redeemed) {
    return (
      <ClaimFrame t={t}>
        <AlreadyRedeemedState data={data} t={t} lang={lang} />
      </ClaimFrame>
    );
  }
  if (data.expired) {
    return (
      <ClaimFrame t={t}>
        <ExpiredState data={data} t={t} />
      </ClaimFrame>
    );
  }
  if (!data.usable_now) {
    return (
      <ClaimFrame t={t}>
        <NotYetUsableState data={data} t={t} lang={lang} />
      </ClaimFrame>
    );
  }
  // Fresh redemption (just_redeemed=true) — default success screen
  return (
    <ClaimFrame t={t}>
      <WinState data={data} t={t} />
    </ClaimFrame>
  );
}

/* ------------------------------------------------------------------ */
/* Shell — holographic noir background + footer                        */
/* ------------------------------------------------------------------ */
function ClaimFrame({ t, children }) {
  return (
    <div
      className="relative min-h-screen overflow-hidden px-4 py-10 text-white sm:px-6 sm:py-16"
      style={{
        background:
          "radial-gradient(1200px 600px at 10% -10%, rgba(168,85,247,0.22), transparent 60%), radial-gradient(900px 500px at 100% 10%, rgba(34,211,238,0.18), transparent 55%), linear-gradient(180deg,#05060A 0%,#070912 45%,#05060A 100%)",
      }}
      data-testid="claim-page"
    >
      {/* Grain overlay for that premium print-feel */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.07] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.6 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
        }}
      />

      <div className="relative mx-auto w-full max-w-[540px]">
        <div className="mb-8 flex items-center justify-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 backdrop-blur-xl">
            <Sparkles className="h-3.5 w-3.5 text-[#A78BFA]" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.32em] text-white/80">
              {t("claim.badge")}
            </span>
          </div>
        </div>
        {children}

        <div className="mt-10 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[11px] text-white/50">
          <Link
            to="/conditions#mentions-legales"
            className="hover:text-white/80 hover:underline"
            data-testid="claim-footer-legal"
          >
            {t("play.legal_notice")}
          </Link>
          <span className="opacity-40">·</span>
          <Link
            to="/conditions#confidentialite"
            className="hover:text-white/80 hover:underline"
            data-testid="claim-footer-privacy"
          >
            {t("play.legal_privacy")}
          </Link>
          <span className="opacity-40">·</span>
          <Link
            to="/organiser-un-concours"
            className="hover:text-white/80 hover:underline"
            data-testid="claim-footer-contact"
          >
            {t("play.legal_run_contest")}
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* States                                                             */
/* ------------------------------------------------------------------ */
function WinState({ data, t }) {
  const firstName = data.first_name ? data.first_name : "";
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      className="relative overflow-hidden rounded-[28px] border border-white/10 bg-white/[0.04] p-6 sm:p-10 backdrop-blur-2xl"
      style={{
        boxShadow:
          "0 30px 80px -40px rgba(168,85,247,0.55), 0 0 0 1px rgba(255,255,255,0.06) inset",
      }}
      data-testid="claim-win-state"
    >
      {/* Holographic ring behind the icon */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 mx-auto h-56 w-56 -translate-y-20 rounded-full blur-3xl"
        style={{
          background:
            "conic-gradient(from 180deg at 50% 50%, #A78BFA, #22D3EE, #F472B6, #A78BFA)",
          opacity: 0.35,
        }}
      />

      {/* Merchant logo badge */}
      {data.merchant_logo ? (
        <div className="relative flex justify-center">
          <div className="relative rounded-full border border-white/15 bg-white/80 p-2 shadow-xl">
            <img
              src={absUrl(data.merchant_logo)}
              alt={data.merchant_name || "Logo"}
              className="h-16 w-16 rounded-full object-contain"
              data-testid="claim-merchant-logo"
            />
          </div>
        </div>
      ) : (
        <div className="relative flex justify-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full border border-white/15 bg-gradient-to-br from-[#A78BFA] to-[#22D3EE] shadow-xl">
            <PartyPopper className="h-9 w-9 text-white" />
          </div>
        </div>
      )}

      <div className="relative mt-6 text-center">
        <div className="text-[11px] font-semibold uppercase tracking-[0.35em] text-white/50">
          {firstName ? t("claim.bravo_name", { name: firstName }) : t("claim.bravo")}
        </div>
        <h1
          className="mt-2 text-3xl font-extrabold leading-tight sm:text-4xl"
          data-testid="claim-title"
        >
          {t("claim.title")}
        </h1>
        <p className="mt-3 text-white/70 text-sm sm:text-base">
          {t("claim.at")}{" "}
          <span
            className="font-semibold text-white"
            data-testid="claim-merchant-name"
          >
            {data.merchant_name || "—"}
          </span>
        </p>
      </div>

      {/* Prize label — holographic chip */}
      <div className="relative mt-6 flex justify-center">
        <div
          className="rounded-full px-5 py-2 text-sm font-bold text-[#05060A]"
          style={{
            background:
              "linear-gradient(90deg,#F4E0A6 0%,#F3B678 35%,#E8AFD6 70%,#B1D9F5 100%)",
            boxShadow: "0 10px 30px -12px rgba(243,182,120,0.55)",
          }}
          data-testid="claim-prize"
        >
          🎁 &nbsp;{data.prize_label || t("claim.default_prize")}
        </div>
      </div>

      {/* Coupon code — boarding-pass style */}
      <div className="relative mt-8">
        <div className="mb-2 text-center text-[10px] font-semibold uppercase tracking-[0.35em] text-white/50">
          {t("claim.code_label")}
        </div>
        <div
          className="rounded-2xl border border-white/10 bg-black/60 p-5 text-center"
          style={{
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)",
          }}
        >
          <div
            className="font-mono text-2xl font-extrabold tracking-[0.35em] text-white select-all sm:text-3xl"
            data-testid="claim-code"
          >
            {data.coupon_code || "—"}
          </div>
        </div>
        <div className="mt-3 text-center text-xs text-white/60">
          {t("claim.show_screen")}
        </div>
      </div>

      {/* Redeemed confirmation pill */}
      <div
        className="relative mt-7 flex items-center justify-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-4 py-2.5 text-sm font-semibold text-emerald-200"
        data-testid="claim-redeemed-pill"
      >
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
        </span>
        {t("claim.redeemed_pill")}
      </div>

      {data.receipt_url && (
        <div className="relative mt-6">
          <ClaimReceiptBlock receipt={data} t={t} />
        </div>
      )}

      <div className="relative mt-8 text-center text-[10px] font-semibold uppercase tracking-[0.4em] text-white/40">
        {t("claim.game_by")}
      </div>
    </motion.div>
  );
}

function AlreadyRedeemedState({ data, t, lang }) {
  const whenStr = data.redeemed_at
    ? new Date(data.redeemed_at).toLocaleString(lang === "en" ? "en-GB" : "fr-FR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="relative overflow-hidden rounded-[28px] border border-white/10 bg-white/[0.04] p-8 backdrop-blur-2xl sm:p-10"
      style={{
        boxShadow:
          "0 30px 80px -40px rgba(34,211,238,0.35), 0 0 0 1px rgba(255,255,255,0.06) inset",
      }}
      data-testid="claim-already-redeemed-state"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 mx-auto h-52 w-52 -translate-y-20 rounded-full blur-3xl"
        style={{
          background:
            "linear-gradient(135deg,#22D3EE 0%,#A78BFA 100%)",
          opacity: 0.3,
        }}
      />

      <div className="relative flex justify-center">
        <div className="flex h-20 w-20 items-center justify-center rounded-full border border-white/15 bg-white/10 backdrop-blur-xl">
          <Medal className="h-9 w-9 text-[#F4E0A6]" />
        </div>
      </div>

      <div className="relative mt-6 text-center">
        <div className="text-[11px] font-semibold uppercase tracking-[0.35em] text-white/50">
          {t("claim.already_eyebrow")}
        </div>
        <h1
          className="mt-2 text-3xl font-extrabold leading-tight sm:text-[34px]"
          data-testid="claim-already-title"
        >
          {t("claim.already_title")}
        </h1>
        <p className="mt-4 text-white/75 leading-relaxed">
          {t("claim.already_body_1")}{" "}
          <br className="hidden sm:block" />
          {t("claim.already_body_2")}
        </p>
        {whenStr && (
          <div
            className="mt-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white/70"
            data-testid="claim-already-when"
          >
            <Clock className="h-3.5 w-3.5" />
            {t("claim.collected_on", { date: whenStr })}
          </div>
        )}
        {data.merchant_name && (
          <div className="mt-3 text-xs text-white/50">
            {t("claim.at")} <span className="text-white/80">{data.merchant_name}</span>
          </div>
        )}
      </div>

      <div className="relative mt-8 text-center text-[10px] font-semibold uppercase tracking-[0.4em] text-white/40">
        {t("claim.game_by")}
      </div>
    </motion.div>
  );
}

function NotYetUsableState({ data, t, lang }) {
  const date = data.usable_from
    ? new Date(data.usable_from).toLocaleDateString(lang === "en" ? "en-GB" : "fr-FR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    : null;
  const modeLabel =
    data.usable_from_mode === "next_monday"
      ? t("claim.when_next_monday")
      : data.usable_from_mode === "tomorrow"
        ? t("claim.when_tomorrow")
        : date
          ? t("claim.when_date", { date })
          : t("claim.when_soon");
  return (
    <div
      className="rounded-[28px] border border-white/10 bg-white/[0.04] p-8 text-center backdrop-blur-2xl"
      data-testid="claim-not-yet-state"
    >
      <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border border-white/15 bg-white/10">
        <Clock className="h-9 w-9 text-[#A78BFA]" />
      </div>
      <h1 className="mt-6 text-2xl font-extrabold">{t("claim.soon_title")}</h1>
      <p className="mt-3 text-white/70">
        <Trans t={t} i18nKey="claim.soon_body_1" values={{ when: modeLabel }} components={{ strong: <strong /> }} />
        <br />
        {t("claim.soon_body_2")}
      </p>
    </div>
  );
}

function ExpiredState({ data, t }) {
  return (
    <div
      className="rounded-[28px] border border-white/10 bg-white/[0.04] p-8 text-center backdrop-blur-2xl"
      data-testid="claim-expired-state"
    >
      <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border border-rose-400/30 bg-rose-400/10">
        <CalendarX2 className="h-9 w-9 text-rose-300" />
      </div>
      <h1 className="mt-6 text-2xl font-extrabold">{t("claim.expired_title")}</h1>
      <p className="mt-3 text-white/70">
        {t("claim.expired_body")}
        {data.merchant_name && (
          <>
            {" "}
            <Trans t={t} i18nKey="claim.expired_contact" values={{ merchant: data.merchant_name }} components={{ strong: <strong /> }} />
          </>
        )}
      </p>
    </div>
  );
}

function InvalidState({ message, t }) {
  return (
    <div
      className="rounded-[28px] border border-white/10 bg-white/[0.04] p-8 text-center backdrop-blur-2xl"
      data-testid="claim-invalid-state"
    >
      <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border border-white/10 bg-white/5">
        <AlertTriangle className="h-9 w-9 text-white/70" />
      </div>
      <h1 className="mt-6 text-2xl font-extrabold">{t("claim.invalid_title")}</h1>
      <p className="mt-3 text-sm text-white/70">{playerErrorMessage(message, t("errors.claim_not_found"))}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */
function absUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//.test(url)) return url;
  const base = API.replace(/\/api$/, "");
  return `${base}${url}`;
}

function ClaimReceiptBlock({ receipt, t }) {
  const ocr = receipt?.receipt_ocr;
  const url = receipt?.receipt_url;
  if (!url) return null;
  const fullUrl = absUrl(url);
  return (
    <div
      className="rounded-2xl border border-white/10 bg-black/40 p-3 backdrop-blur-xl"
      data-testid="claim-receipt-block"
    >
      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-white/60">
        <Receipt className="h-3.5 w-3.5" />
        {t("claim.receipt")}
      </div>
      {ocr && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {ocr.total_amount != null && (
            <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-semibold text-white/90">
              <Euro className="h-3 w-3" />
              {Number(ocr.total_amount).toFixed(2)} {ocr.currency || "EUR"}
            </span>
          )}
          {ocr.date && (
            <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[11px] text-white/80">
              <Calendar className="h-3 w-3" />
              {ocr.date}
            </span>
          )}
          {ocr.merchant_name && (
            <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[11px] text-white/80">
              <Store className="h-3 w-3" />
              <span className="max-w-[18ch] truncate">{ocr.merchant_name}</span>
            </span>
          )}
        </div>
      )}
      <a
        href={fullUrl}
        target="_blank"
        rel="noreferrer"
        className="mt-3 block overflow-hidden rounded-xl border border-white/10 bg-white/5"
      >
        <img
          src={fullUrl}
          alt="Ticket"
          className="max-h-72 w-full object-contain"
        />
      </a>
    </div>
  );
}
